import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  MapPin,
  RefreshCw,
  Download,
  Printer,
  Loader2,
  X,
  AlertTriangle,
  Tag,
  CreditCard,
  RotateCcw,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { ConfirmDialog } from '@components/Dialog';
import { ReturnInvoiceModal } from '@components/ReturnInvoiceModal';
import { Tabs, TabItem } from '@components/Tabs';
import { usePermissions } from '@/hooks/usePermissions';
import { invoiceService } from '../../services/invoiceService';
import {
  Invoice,
  INVOICE_STATUS_OPTIONS,
  InvoiceEmitError,
  InvoiceEmitErrorField,
  InvoiceInstallment,
  InvoiceLink,
  getInvoiceModelLabel,
} from '../../types/invoice';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR');
};

const formatDocument = (doc?: string) => {
  if (!doc) return '—';
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

const formatAccessKey = (key?: string) => {
  if (!key) return '—';
  return key.replace(/(.{4})/g, '$1 ').trim();
};

const STATUS_BADGE_CLASSES: Record<number, string> = {
  1:  'bg-yellow-100 text-yellow-800',
  2:  'bg-red-100 text-red-700',
  3:  'bg-orange-100 text-orange-700',
  4:  'bg-red-200 text-red-800',
  5:  'bg-green-100 text-green-700',
  6:  'bg-emerald-100 text-emerald-700',
  7:  'bg-blue-100 text-blue-700',
  8:  'bg-orange-200 text-orange-800',
  9:  'bg-slate-200 text-slate-700',
  10: 'bg-cyan-100 text-cyan-700',
  11: 'bg-purple-100 text-purple-700',
};

const getStatusBadge = (status?: number) => {
  if (status === undefined || status === null) return <span className="text-slate-500">—</span>;
  const opt = INVOICE_STATUS_OPTIONS.find(o => o.value === status);
  const label = opt?.label ?? `Status ${status}`;
  const cls = STATUS_BADGE_CLASSES[status] ?? 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};

// ─── Field display ───────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs text-slate-500 uppercase tracking-wider">{label}</dt>
    <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
  </div>
);

// ─── Related invoices (vínculo devolução ↔ venda) ────────────────

const RelatedInvoices: React.FC<{
  title: string;
  invoices: InvoiceLink[];
  onOpen: (invoiceId: number) => void;
}> = ({ title, invoices, onOpen }) => (
  <div>
    <dt className="text-xs text-slate-500 uppercase tracking-wider mb-2">{title}</dt>
    <div className="space-y-2">
      {invoices.map(link => (
        <div
          key={link.invoice_id}
          className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2"
        >
          <button
            className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-200"
            title={`Abrir a nota fiscal #${link.number}`}
            onClick={() => onOpen(link.invoice_id)}
          >
            <RotateCcw size={12} />
            NF #{link.number}
            <ExternalLink size={10} />
          </button>
          <span className="text-xs text-slate-600">Série {link.serie}</span>
          <span className="text-xs text-slate-600">{formatDateTime(link.generation_timestamp ?? undefined)}</span>
          <span className="text-xs font-medium text-slate-800">{formatCurrency(link.total_amount ?? undefined)}</span>
          {link.status && <span className="text-xs text-slate-600">{link.status}</span>}
          {link.access_key && (
            <span className="font-mono text-[11px] break-all text-slate-500">{formatAccessKey(link.access_key)}</span>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ─── Send Error Fields (recursive) ───────────────────────────────

const SendErrorFields: React.FC<{ fields: InvoiceEmitErrorField[]; depth?: number }> = ({ fields, depth = 0 }) => (
  <ul className={`space-y-2 ${depth > 0 ? 'ml-4 border-l-2 border-red-200 pl-3' : ''}`}>
    {fields.map((f, i) => (
      <li key={i} className="text-sm">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium text-red-800">{f.element}</span>
            {f.code ? <span className="text-red-400 ml-1">(código {f.code})</span> : null}
            <p className="text-red-700">{f.msg}</p>
            {f.collection && f.collection.length > 0 && <SendErrorFields fields={f.collection} depth={depth + 1} />}
          </div>
        </div>
      </li>
    ))}
  </ul>
);

// ─── Component ───────────────────────────────────────────────────

export const InvoiceDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { checkPermission } = usePermissions();

  // Determine where the back button should go based on navigation origin
  const backPath = (location.state as { from?: string })?.from || '/invoices';

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Actions state
  const [syncing, setSyncing] = useState(false);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<InvoiceEmitError | null>(null);
  const [unlinkingOrder, setUnlinkingOrder] = useState(false);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelJust, setCancelJust] = useState('');
  const [installments, setInstallments] = useState<InvoiceInstallment[]>([]);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canWrite = (checkPermission('invoices.write').hasPermission || checkPermission('*').hasPermission);

  const isPending = invoice?.status === 1;
  const isCancelled = invoice?.status === 2;
  const isRejected = invoice?.status === 4;
  const isAuthorized = invoice?.status === 5;
  const canUnlinkOrder = isPending || isRejected || isCancelled;
  // Devolução: mesma regra do botão da listagem — toda NF de SAÍDA autorizada/emitida. Uma venda pode
  // ter mais de uma devolução (parcial em etapas), então uma devolução existente não esconde o botão.
  const canGenerateReturn = invoice?.type !== 'E' && (invoice?.status === 5 || invoice?.status === 6);


  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const data = await invoiceService.getInvoiceById(parseInt(id));
        setInvoice(data);
        try {
          setInstallments(await invoiceService.getInvoiceInstallments(parseInt(id)));
        } catch {
          setInstallments([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar nota fiscal');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // ─── Actions ──────────────────────────────────────────────────

  const handleReturnInvoiceSuccess = async (message: string) => {
    setReturnModalOpen(false);
    setSuccessMessage(message);
    if (!id) return;
    // Recarrega para "Documentos Relacionados" listar a devolução recém-criada.
    try {
      setInvoice(await invoiceService.getInvoiceById(parseInt(id)));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao recarregar a nota fiscal');
    }
  };

  const handleCancelInvoice = async () => {
    if (!id) return;
    try {
      setCancelling(true);
      setActionError(null);
      setSendError(null);
      const result = await invoiceService.cancelInvoice(parseInt(id), cancelJust.trim());
      if (result.success) {
        setCancelOpen(false);
        setCancelJust('');
        const updated = await invoiceService.getInvoiceById(parseInt(id));
        setInvoice(updated);
      } else if (result.error && typeof result.error === 'object') {
        setCancelOpen(false);
        setSendError(result.error);
      } else {
        setActionError(result.message || (typeof result.error === 'string' ? result.error : 'Resposta inesperada do servidor.'));
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao cancelar nota fiscal');
    } finally {
      setCancelling(false);
    }
  };

  const handleSyncInvoice = async () => {
    if (!id || !invoice) return;
    try {
      setSyncing(true);
      setActionError(null);
      const result = await invoiceService.syncInvoice(parseInt(id), invoice.company_id);
      if (result.success) {
        // Refetch full invoice data to reflect sync changes
        const updated = await invoiceService.getInvoiceById(parseInt(id));
        setInvoice(updated);
      } else {
        setActionError(result.error?.description || result.error?.message || 'Erro ao sincronizar nota fiscal');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao sincronizar nota fiscal');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadXml = async () => {
    if (!id) return;
    try {
      setXmlLoading(true);
      setActionError(null);
      await invoiceService.downloadXml(parseInt(id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao baixar XML');
    } finally {
      setXmlLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      setPdfLoading(true);
      setActionError(null);
      await invoiceService.downloadPdf(parseInt(id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadShippingLabel = async () => {
    if (!id) return;
    try {
      setLabelLoading(true);
      setActionError(null);
      await invoiceService.downloadShippingLabel(parseInt(id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao gerar etiqueta de envio');
    } finally {
      setLabelLoading(false);
    }
  };

  const handleUnlinkOrder = async () => {
    if (!id) return;
    try {
      setUnlinkingOrder(true);
      setActionError(null);
      await invoiceService.unlinkOrder(parseInt(id));
      setUnlinkConfirmOpen(false);
      // Reload invoice data
      const updated = await invoiceService.getInvoiceById(parseInt(id));
      setInvoice(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao desvincular pedido');
    } finally {
      setUnlinkingOrder(false);
    }
  };





  // Loading
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate(backPath)}><ArrowLeft size={20} /></Button>
          <div className="h-8 bg-slate-200 rounded w-56 animate-pulse" />
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-emerald-600" size={32} />
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate(backPath)}><ArrowLeft size={20} /></Button>
          <h1 className="text-2xl font-bold text-slate-900">Nota fiscal não encontrada</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error || 'Nota fiscal não encontrada.'}</div>
      </div>
    );
  }

  const items = invoice.items || [];
  // Vínculo devolução ↔ venda: a API preenche as duas listas só no detalhe da nota.
  const returnInvoices = invoice.return_invoices || [];
  const referencedInvoices = invoice.referenced_invoices || [];

  // ── Tab definitions ──────────────────────────────────────────

  const itemsTab: TabItem = {
    id: 'items',
    label: `Itens (${items.length})`,
    icon: <Package size={16} />,
    content: (
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Código</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">NCM</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">CFOP</th>
                  <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">UN</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Qtd</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor Unit.</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.invoice_item_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-4 font-medium text-slate-900">{item.code || '—'}</td>
                    <td className="py-2 px-4 text-slate-600">{item.description || '—'}</td>
                    <td className="py-2 px-4 text-slate-600 text-xs">{item.ncm || '—'}</td>
                    <td className="py-2 px-4 text-slate-600 text-xs">{item.cfop || '—'}</td>
                    <td className="py-2 px-4 text-center text-slate-600">{item.unit || '—'}</td>
                    <td className="py-2 px-4 text-right">{item.quantity}</td>
                    <td className="py-2 px-4 text-right">{formatCurrency(item.unit_value)}</td>
                    <td className="py-2 px-4 text-right font-medium">
                      {formatCurrency(item.total_value ?? item.unit_value * item.quantity)}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-400">Nenhum item</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td colSpan={6} className="py-3 px-4 text-right font-semibold text-slate-700 uppercase text-sm">Valor Produtos</td>
                  <td className="py-3 px-4 text-right font-bold text-slate-900">{formatCurrency(invoice.product_amount)}</td>
                  <td />
                </tr>
                <tr className="border-t border-slate-300">
                  <td colSpan={6} className="py-3 px-4 text-right font-bold text-slate-900 uppercase">Total Nota Fiscal</td>
                  <td className="py-3 px-4 text-right font-bold text-emerald-700 text-lg">{formatCurrency(invoice.total_amount)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    ),
  };

  const deliveryTab: TabItem = {
    id: 'delivery',
    label: 'Dados de Entrega',
    icon: <MapPin size={16} />,
    content: (
      <Card>
        <CardHeader title="Endereço de Entrega" />
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Logradouro">
              {invoice.ship_addr_street || '—'}{invoice.ship_addr_num ? `, ${invoice.ship_addr_num}` : ''}
            </Field>
            {invoice.ship_addr_compl && <Field label="Complemento">{invoice.ship_addr_compl}</Field>}
            <Field label="Bairro">{invoice.ship_addr_district || '—'}</Field>
            <Field label="Cidade/UF">
              {invoice.ship_addr_city || '—'} - {invoice.ship_addr_state || '—'}
            </Field>
            <Field label="CEP">{invoice.ship_addr_zip || '—'}</Field>
            <Field label="E-mail">{invoice.ship_cust_email || '—'}</Field>
            <Field label="Transportadora">{invoice.shipping_service_name || '—'}</Field>
            <Field label="Qtd. Volumes">{invoice.volume_quantity ?? '—'}</Field>
            <Field label="Peso Total">{invoice.total_weight ? `${invoice.total_weight} kg` : '—'}</Field>
          </dl>
        </CardContent>
      </Card>
    ),
  };

  const installmentsTotal = installments.reduce((sum, inst) => sum + (inst.value || 0), 0);

  const installmentsTab: TabItem = {
    id: 'installments',
    label: `Parcelas (${installments.length})`,
    icon: <CreditCard size={16} />,
    content: (
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Forma de Pagamento</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Vencimento</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Observação</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor</th>
                </tr>
              </thead>
              <tbody>
                {installments.map((inst, idx) => (
                  <tr key={inst.invoice_installment_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-4 text-slate-600">{idx + 1}</td>
                    <td className="py-2 px-4 font-medium text-slate-900">{inst.payment_method_name || '—'}</td>
                    <td className="py-2 px-4 text-slate-600">{inst.due_date || '—'}</td>
                    <td className="py-2 px-4 text-slate-600">{inst.notes || '—'}</td>
                    <td className="py-2 px-4 text-right font-medium">{formatCurrency(inst.value)}</td>
                  </tr>
                ))}
                {installments.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-400">Nenhuma parcela registrada</td>
                  </tr>
                )}
              </tbody>
              {installments.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={4} className="py-3 px-4 text-right font-bold text-slate-900 uppercase">Total</td>
                    <td className="py-3 px-4 text-right font-bold text-emerald-700">{formatCurrency(installmentsTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>
    ),
  };

  const tabs: TabItem[] = [itemsTab, installmentsTab, deliveryTab];

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate(backPath)}><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              NF-e #{invoice.number} — Série {invoice.serie}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              ID: {invoice.invoice_id}
              {invoice.erp_invoice_id && ` | ERP: ${invoice.erp_invoice_id}`}
              {invoice.order_num_ecommerce && ` | Pedido E-commerce: ${invoice.order_num_ecommerce}`}
              {' | '}Gerada em {formatDateTime(invoice.generation_timestamp)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Download XML — not available for Pendente */}
          {!isPending && (
            <Button variant="outline" icon={<Download size={18} />} loading={xmlLoading} onClick={handleDownloadXml}>
              <span className="hidden sm:inline">Baixar XML</span>
              <span className="sm:hidden">XML</span>
            </Button>
          )}
          {/* Print PDF / DANFE — not available for Pendente */}
          {!isPending && (
            <Button variant="outline" icon={<Printer size={18} />} loading={pdfLoading} onClick={handleDownloadPdf}>
              <span className="hidden sm:inline">Imprimir PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
          )}
          {/* Shipping Label */}
          <PermissionGuard permission="invoices.read">
            <Button variant="outline" icon={<Tag size={18} />} loading={labelLoading} onClick={handleDownloadShippingLabel}>
              <span className="hidden sm:inline">Imprimir Etiqueta</span>
              <span className="sm:hidden">Etiqueta</span>
            </Button>
          </PermissionGuard>
          {/* Consultar situação na SEFAZ (consulta por chave) */}
          <PermissionGuard permission="invoices.emit">
            <Button variant="outline" icon={<RefreshCw size={18} />} loading={syncing} onClick={handleSyncInvoice}>
              <span className="hidden sm:inline">Consultar situação na SEFAZ</span>
              <span className="sm:hidden">Consultar</span>
            </Button>
          </PermissionGuard>
          {/* Unlink order — when invoice is pending (status=1), rejected (status=4) or cancelled (status=2) */}
          {canUnlinkOrder && canWrite && (
            <Button variant="outline" icon={<X size={18} />} loading={unlinkingOrder} onClick={() => setUnlinkConfirmOpen(true)} className="text-red-600 border-red-300 hover:bg-red-50">
              Desvincular Pedido
            </Button>
          )}
          {/* Nota de devolução com esta NF como origem (itens, cliente e chave já preenchidos) */}
          {canGenerateReturn && (
            <PermissionGuard permission="invoices.create">
              <Button variant="outline" icon={<RotateCcw size={18} />} onClick={() => setReturnModalOpen(true)}>
                <span className="hidden sm:inline">Gerar nota de devolução</span>
                <span className="sm:hidden">Devolução</span>
              </Button>
            </PermissionGuard>
          )}
          {/* Cancel invoice — only when authorized (status=5), SEFAZ direto (evento 110111) */}
          {isAuthorized && (
            <PermissionGuard permission="invoices.emit">
              <Button variant="outline" icon={<X size={18} />} onClick={() => { setCancelJust(''); setCancelOpen(true); }} className="text-red-600 border-red-300 hover:bg-red-50">
                <span className="hidden sm:inline">Cancelar Nota</span>
                <span className="sm:hidden">Cancelar</span>
              </Button>
            </PermissionGuard>
          )}
        </div>
      </div>

      {/* Cancel invoice modal (justificativa, 15..255 chars) */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
              <h2 className="text-lg font-bold text-red-900">Cancelar Nota Fiscal</h2>
              <button onClick={() => setCancelOpen(false)} className="p-1.5 hover:bg-red-100 rounded-full">
                <X size={18} className="text-red-600" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-slate-600">
                Cancelar a NF-e #{invoice.number} (série {invoice.serie}) junto à SEFAZ (evento 110111). Esta ação é irreversível.
              </p>
              <label className="block text-sm font-medium text-slate-700">Justificativa (15 a 255 caracteres)</label>
              <textarea
                value={cancelJust}
                onChange={(e) => setCancelJust(e.target.value)}
                rows={3}
                maxLength={255}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder="Motivo do cancelamento"
              />
              <p className="text-xs text-slate-400">{cancelJust.trim().length}/255</p>
            </div>
            <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelOpen(false)}>Voltar</Button>
              <Button
                loading={cancelling}
                disabled={cancelJust.trim().length < 15}
                onClick={handleCancelInvoice}
                className="bg-red-600 hover:bg-red-700"
              >
                Confirmar Cancelamento
              </Button>
            </div>
          </div>
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {actionError}
          <button className="ml-2 underline" onClick={() => setActionError(null)}>Fechar</button>
        </div>
      )}

      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
          {successMessage}
          <button className="ml-2 underline" onClick={() => setSuccessMessage(null)}>Fechar</button>
        </div>
      )}

      {/* Header cards: Nota + Cliente + Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Dados da Nota */}
        <Card>
          <CardHeader title="Dados da Nota" />
          <CardContent>
            <dl className="space-y-2">
              <Field label="Número"><span className="font-medium">{invoice.number}</span></Field>
              <Field label="Série">{invoice.serie}</Field>
              <Field label="Modelo">{getInvoiceModelLabel(invoice.model)}</Field>
              <Field label="Tipo">{invoice.type || '—'}</Field>
              <Field label="Natureza da Operação">{invoice.invoice_transaction_type_name || '—'}</Field>
              <Field label="Empresa">{invoice.company_name || '—'}</Field>
              <Field label="CNPJ Empresa">{invoice.company_document ? formatDocument(invoice.company_document) : '—'}</Field>
              <Field label="Chave de Acesso">
                <span className="font-mono text-xs break-all">{formatAccessKey(invoice.access_key)}</span>
              </Field>
              <Field label="Simples Nacional">{invoice.is_simple_tax ? 'Sim' : 'Não'}</Field>
            </dl>
          </CardContent>
        </Card>

        {/* Cliente */}
        <Card>
          <CardHeader title="Cliente" />
          <CardContent>
            <dl className="space-y-2">
              <Field label="Nome"><span className="font-medium">{invoice.customer_name || '—'}</span></Field>
              <Field label="Documento"><span>{formatDocument(invoice.customer_document)}</span></Field>
              <Field label="Telefone">{invoice.customer_phone || '—'}</Field>
              <Field label="Endereço">{invoice.customer_address || '—'}</Field>
            </dl>
          </CardContent>
        </Card>

        {/* Status e Valores */}
        <Card>
          <CardHeader title="Situação e Valores" />
          <CardContent>
            <dl className="space-y-2">
              <Field label="Situação">{getStatusBadge(invoice.status)}</Field>
              <Field label="Valor Produtos">{formatCurrency(invoice.product_amount)}</Field>
              <Field label="Valor Total">
                <span className="font-bold text-emerald-700 text-lg">{formatCurrency(invoice.total_amount)}</span>
              </Field>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Documentos relacionados: vínculo devolução ↔ venda ([Financial].[InvoiceReference]).
          Os dois sentidos ficam na mesma tela — a NF de venda mostra a(s) devolução(ões) e a NF de
          devolução mostra a venda de origem. Listas, porque uma venda pode ter mais de uma
          devolução (devolução parcial em etapas). */}
      {(returnInvoices.length > 0 || referencedInvoices.length > 0) && (
        <Card>
          <CardHeader title="Documentos Relacionados" />
          <CardContent>
            <div className="space-y-4">
              {returnInvoices.length > 0 && (
                <RelatedInvoices
                  title={returnInvoices.length > 1 ? 'Notas de devolução desta venda' : 'Nota de devolução desta venda'}
                  invoices={returnInvoices}
                  onOpen={id => navigate(`/invoices/${id}`)}
                />
              )}
              {referencedInvoices.length > 0 && (
                <RelatedInvoices
                  title={referencedInvoices.length > 1 ? 'Notas de venda de origem' : 'Nota de venda de origem'}
                  invoices={referencedInvoices}
                  onOpen={id => navigate(`/invoices/${id}`)}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs tabs={tabs} defaultTab="items" />

      {/* Audit info */}
      <div className="text-xs text-slate-400 flex flex-wrap gap-4">
        <span>Criado em: {formatDateTime(invoice.created_at)} por {invoice.created_by || '—'}</span>
        <span>Atualizado em: {formatDateTime(invoice.updated_at)} por {invoice.last_updated_by || '—'}</span>
      </div>

      {/* Nota de devolução pré-preenchida com esta NF de venda */}
      {returnModalOpen && (
        <ReturnInvoiceModal
          isOpen={returnModalOpen}
          onClose={() => setReturnModalOpen(false)}
          onSuccess={handleReturnInvoiceSuccess}
          sourceInvoice={invoice}
        />
      )}

      {/* Unlink order confirm dialog */}
      <ConfirmDialog
        isOpen={unlinkConfirmOpen}
        onClose={() => setUnlinkConfirmOpen(false)}
        onConfirm={handleUnlinkOrder}
        title="Desvincular Pedido"
        message={`Deseja desvincular o pedido de venda desta nota fiscal ${isRejected ? 'rejeitada' : isCancelled ? 'cancelada' : 'pendente'} #${invoice.number}? Isso permitirá gerar uma nova nota fiscal para o pedido.`}
        confirmText="Desvincular"
        cancelText="Cancelar"
        severity="danger"
        loading={unlinkingOrder}
      />

      {/* Error modal (detailed SEFAZ validation errors, e.g. cancelamento) */}
      {sendError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-red-600" />
                <h2 className="text-lg font-bold text-red-900">Erro na Nota Fiscal</h2>
              </div>
              <button onClick={() => setSendError(null)} className="p-1.5 hover:bg-red-100 rounded-full transition-colors">
                <X size={18} className="text-red-600" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
              <div>
                <p className="text-sm font-semibold text-slate-700">Tipo: <span className="font-normal text-red-600">{sendError.type}</span></p>
                <p className="text-sm font-semibold text-slate-700 mt-1">Mensagem: <span className="font-normal text-red-700">{sendError.message}</span></p>
                {sendError.description && (
                  <p className="text-sm text-slate-600 mt-2 bg-red-50 p-3 rounded-lg border border-red-100">{sendError.description}</p>
                )}
              </div>
              {sendError.fields && sendError.fields.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Detalhes dos erros:</h3>
                  <SendErrorFields fields={sendError.fields} />
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
              <Button variant="outline" onClick={() => setSendError(null)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
